import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  SaveUserInfoErrorReason,
  UpdateUserInfoRequest,
  UserInfoResponse
} from "@dealer-center/ddc-pages-configuration-interface";
import {
  CONFIGURATION_QUERY_KEY_BASE,
  type ConfigurationWriteRequestError,
  configurationWriteRequest
} from "./configurationRequest";
import { cancelInFlightReads, mergeUserIntoListCache } from "./userCacheWrites";

export interface SaveUserInfoVariables {
  userId: string;
  accountId: string;
  /** Only the changed fields; an omitted field is left unchanged upstream. */
  fields: UpdateUserInfoRequest;
}

/**
 * On success the cache is updated from the variables, not invalidated: CC9 reads lag writes, so a
 * refetch would return the old profile and overwrite the save.
 */
export const useSaveUserInfo = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    ConfigurationWriteRequestError<SaveUserInfoErrorReason>,
    SaveUserInfoVariables
  >({
    mutationFn: ({ userId, accountId, fields }) =>
      configurationWriteRequest<SaveUserInfoErrorReason>(
        { resource: "userinfo", userId, accountId },
        fields
      ),
    onSuccess: async (_data, { userId, accountId, fields }) => {
      const userInfoKey = [
        CONFIGURATION_QUERY_KEY_BASE,
        "userinfo",
        accountId,
        userId
      ];

      await cancelInFlightReads(queryClient, userInfoKey);
      queryClient.setQueryData<UserInfoResponse | undefined>(
        userInfoKey,
        current =>
          current
            ? { ...current, summary: { ...current.summary, ...fields } }
            : undefined
      );
      await mergeUserIntoListCache(queryClient, accountId, userId, fields);
    }
  });
};
